import { Dispatch } from '@reduxjs/toolkit';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import {
  buildMinimalCartEntriesLookup,
  findMatchingCartEntry,
  mapHydratedCart,
  mapLegacyToMinimalCartEntries,
  mapServiceModeToCartServiceMode,
} from 'features/cart/utils/minimal-cart';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  IHydrateCartInput,
  IMenuSelectionInput,
  Platform,
  ServiceMode,
  useHydrateCartMutation,
} from 'generated/graphql-gateway';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions } from 'state/global-state';
import { IOffersState } from 'state/global-state/models/loyalty/offers/offers.types';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';
import { createCartEntriesFromQuantity } from 'utils/cart';
import { isWeb } from 'utils/environment';
import logger from 'utils/logger';
import { blockContentToPlainText } from 'utils/sanity';

import { isOfferCombo, mapLegacyEntriesForHydration } from './use-cart-hydration.ts/utils';
import { IUseCart } from './use-cart-types';

const getCartId = (cartEntry: ILegacyCartEntry): string => {
  // The app heavily relies on the DISCOUNT_OFFER_CART_ID to identify the discount offer. We should not change it.
  const isDiscountOffer = cartEntry.cartId === DISCOUNT_OFFER_CART_ID;
  const isOfferComboEntry = isOfferCombo(cartEntry);

  const cartId = isDiscountOffer
    ? DISCOUNT_OFFER_CART_ID
    : isOfferComboEntry
    ? cartEntry.cartId
    : uuidv4();

  return cartId;
};

const addCartEntriesByQuantity = (
  cartEntry: ILegacyCartEntry,
  quantity: number,
  addItemToCart: IUseCart['addItemToCart']
) => {
  Array.from({ length: quantity }).forEach(() => {
    const cartId = getCartId(cartEntry);
    addItemToCart(
      {
        ...cartEntry,
        quantity: 1,
        cartId,
        lineId: cartId,
      },
      undefined,
      false,
      true
    );
  });
};

// Had to extract this to a pure function to avoid a circular dep between use-cart and use-minimal-cart
export async function hydrateEntries(
  cartEntries: ICartEntry[],
  storeId: string,
  serviceMode: ServiceMode,
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0],
  addItemToCart: IUseCart['addItemToCart'],
  updateCartEntry: IUseCart['updateCartEntry'],
  dispatch: Dispatch,
  handleRewards?: (
    rewardBenefitId: string,
    cartEntry: ILegacyCartEntry,
    remainingPoints: { value: number }
  ) => ILegacyCartEntry,
  stagedCartPoints: number = 0
): Promise<boolean> {
  const channel = isWeb ? Platform.WEB : Platform.APP;
  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));
  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    // Initialize remainingPoints outside the loop
    const remainingPoints = { value: stagedCartPoints };

    hydratedCartEntries.forEach(cartEntry => {
      const matchingMinimalCartEntry = findMatchingCartEntry(cartEntries, cartEntry);
      if (matchingMinimalCartEntry?.isEdit) {
        // If there is only one cart entry to edit
        if (matchingMinimalCartEntry.quantity === 1) {
          updateCartEntry(cartEntry, cartEntry.cartId, true, true);
        } else {
          // if there is more than one cart entry to edit, first we edit the original cart entry
          updateCartEntry({ ...cartEntry, quantity: 1 }, cartEntry.cartId, true, true);

          // Then we add the copies for the rest of entries by quantity with different ids
          addCartEntriesByQuantity(cartEntry, cartEntry.quantity - 1, addItemToCart);
        }
      } else if (handleRewards && matchingMinimalCartEntry?.rewardBenefitId) {
        const rewardBenefitId = matchingMinimalCartEntry.rewardBenefitId;
        // For rewards, we need to create a set of cartEntries based on the quantity.
        // This will ensure we can run one cartEntry at a time to avoid having race condition issues.
        const cartEntriesFromQuantity = createCartEntriesFromQuantity(cartEntry);

        cartEntriesFromQuantity.forEach(cartEntry => {
          // This will create the applied reward for the cartEntry.
          handleRewards(rewardBenefitId, cartEntry, remainingPoints);
          addItemToCart(cartEntry, undefined, false, true);
        });
      } else {
        addCartEntriesByQuantity(cartEntry, cartEntry.quantity, addItemToCart);
      }
    });
    dispatch(actions.ordering.resetMinimalCart());
    return true;
  } catch (error) {
    dispatch(actions.ordering.resetMinimalCart());
    return false;
  }
}

const mapHydratedEntriesRecursively = ({
  hydratedEntries,
  legacyEntries,
  minimalEntries,
}: {
  hydratedEntries: ILegacyCartEntry[];
  legacyEntries: ILegacyCartEntry[];
  minimalEntries: ICartEntry[];
}): ILegacyCartEntry[] => {
  return hydratedEntries.map((hydratedEntry, index) => {
    const matchingMinimalCartEntry = minimalEntries[index];
    const matchingOriginalCartEntry = legacyEntries.find(
      entry => entry.cartId === matchingMinimalCartEntry?.cartId
    );

    return {
      ...hydratedEntry,
      _id: hydratedEntry.sanityId,
      cartId: matchingMinimalCartEntry?.cartId ?? hydratedEntry.lineId,
      lineId: matchingMinimalCartEntry?.cartId ?? hydratedEntry.lineId,
      image: matchingOriginalCartEntry?.image,
      url: matchingOriginalCartEntry.url ?? '',
      children:
        hydratedEntry.children && !!hydratedEntry.children.length
          ? mapHydratedEntriesRecursively({
              hydratedEntries: hydratedEntry.children,
              legacyEntries: matchingOriginalCartEntry?.children ?? [],
              minimalEntries: matchingMinimalCartEntry?.children ?? [],
            })
          : [],
    };
  });
};

export const hydrateLegacyCart = async ({
  legacyCartEntries,
  appliedOffers,
  cmsOffers,
  serviceMode,
  storeId,
  hydrateCart,
}: {
  legacyCartEntries: ILegacyCartEntry[];
  appliedOffers: LoyaltyAppliedOffer[];
  cmsOffers: IOffersState['cmsOffers'];
  serviceMode: ServiceMode;
  storeId: string;
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0];
}): Promise<ILegacyCartEntry[] | null> => {
  const mappedCartEntries = mapLegacyEntriesForHydration(legacyCartEntries, appliedOffers);
  const mappedMinimalCartEntries = mapLegacyToMinimalCartEntries(mappedCartEntries);

  // Checks if there is a current applied offer
  const appliedOffer = appliedOffers.length
    ? cmsOffers.find(offer => offer._id === appliedOffers[0].cmsId)
    : null;

  // For Full Cart Hydration, restore any applied discounts if needed for existing applied offer
  const discountOfferMinimalEntry =
    appliedOffer && isDiscountLoyaltyOffer(appliedOffer)
      ? ([
          {
            cartId: DISCOUNT_OFFER_CART_ID,
            children: [],
            id: appliedOffer._id,
            name: blockContentToPlainText(appliedOffer.name?.localeRaw),
            quantity: 1,
            type: MinimalCartEntryType.discount,
          },
        ] as ICartEntry[])
      : null;

  const minimalCartEntries = [...mappedMinimalCartEntries, ...(discountOfferMinimalEntry ?? [])];

  const channel = isWeb ? Platform.WEB : Platform.APP;

  const menuSelections: IMenuSelectionInput[] = minimalCartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));

  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    return mapHydratedEntriesRecursively({
      hydratedEntries: (hydrationResult?.data?.hydrateCart.cartEntries as ILegacyCartEntry[]) ?? [],
      legacyEntries: legacyCartEntries,
      minimalEntries: minimalCartEntries,
    });
  } catch (error) {
    logger.error({
      message: 'Legacy Cart Entries Hydration Failed',
      newStoreId: storeId,
      cartEntries: JSON.stringify(minimalCartEntries),
      error: JSON.stringify(error),
    });

    return null;
  }
};

interface IHydrateAndReturnEntries {
  cartEntries: ICartEntry[];
  storeId: string;
  serviceMode: ServiceMode;
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0];
}

/**
 * Hydrates minimal cart entries by calling the GraphQL `hydrateCart` mutation
 * and returns the resulting fully hydrated legacy cart entries.
 *
 * @param {IHydrateAndReturnEntries} params - Parameters for hydration.
 * @param {IMinimalCartEntry[]} params.cartEntries - Minimal cart entries to be hydrated.
 * @param {string} params.storeId - The ID of the store to hydrate the cart for.
 * @param {'pickup' | 'delivery'} params.serviceMode - The selected service mode for the cart.
 * @param {(options: { variables: { channel: Platform; input: IHydrateCartInput } }) => Promise<HydrationResponse>} params.hydrateCart - The GraphQL mutation function used to perform hydration.
 *
 * @returns {Promise<ILegacyCartEntry[] | null>} A promise that resolves to hydrated legacy cart entries,
 * or `null` if the hydration process fails.
 */
export const hydrateAndReturnEntries = async ({
  cartEntries,
  storeId,
  serviceMode,
  hydrateCart,
}: IHydrateAndReturnEntries): Promise<ILegacyCartEntry[] | null> => {
  const channel = isWeb ? Platform.WEB : Platform.APP;

  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));

  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    return hydratedCartEntries;
  } catch (error) {
    logger.error({
      message: 'Minimal Cart Entries Hydration Failed',
      newStoreId: storeId,
      cartEntries: JSON.stringify(cartEntries),
      error: JSON.stringify(error),
    });

    return null;
  }
};
